import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { authService, hotelService, api } from '../../services/api';
import { useUser } from '../../hooks/useUser';
import { notificationService } from '../../services/notificationService';
import ReservationModal from '../../pages/reservations/components/ReservationModal.jsx';
import NotificationModal from '../../pages/reservations/components/NotificationModal';

const Header = ({ onCreateReservation, onMenuClick }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Refs para melhor performance
  const hotelDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const notificationsDropdownRef = useRef(null);
  
  // Estados principais
  const [selectedHotel, setSelectedHotel] = useState('');
  const [hotels, setHotels] = useState([]);
  const [showHotelDropdown, setShowHotelDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [isChangingHotel, setIsChangingHotel] = useState(false);
  const [changingHotelName, setChangingHotelName] = useState('');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    type: 'success',
    title: '',
    message: '',
    details: null
  });
  
  // Carregar hotel selecionado do localStorage imediatamente
  const [initialHotel, setInitialHotel] = useState(null);
  
  // Carregar hotel inicial
  useEffect(() => {
    try {
      const hotel = authService.getSelectedHotel();
      setInitialHotel(hotel);
    } catch (e) {
      console.error('Erro ao carregar hotel inicial:', e);
      setInitialHotel(null);
    }
  }, []);

  // Memoized values para performance
  const unreadCount = useMemo(() => 
    notifications.filter(n => n.unread).length, 
    [notifications]
  );

  const currentHotel = useMemo(() => 
    hotels.find(h => h.name === selectedHotel), 
    [hotels, selectedHotel]
  );

  const isAuthenticated = useMemo(() => {
    const token = localStorage.getItem('token');
    return !!token;
  }, []);

  // Sanitização de dados para segurança
  const sanitizeInput = useCallback((input) => {
    if (typeof input !== 'string') return '';
    return input.replace(/[<>]/g, '').trim();
  }, []);

  const validateHotelData = useCallback((hotel) => {
    if (!hotel || typeof hotel !== 'object') return false;
    return hotel.id && hotel.name && typeof hotel.name === 'string';
  }, []);

  // Funções otimizadas com useCallback
  const showNotificationModal = useCallback((type, title, message, details = null) => {
    setNotificationData({ type, title, message, details });
    setShowNotification(true);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setShowNotification(false);
  }, []);

  // Carregar hotéis da API com cache e tratamento de erro robusto
  const loadHotels = useCallback(async () => {
    if (!isAuthenticated) {
      setLoadingHotels(false);
      return;
    }

    try {
      setLoadingHotels(true);
      const hotelsData = await hotelService.getAll();
      
      // Validar e sanitizar dados dos hotéis
      const validatedHotels = hotelsData.filter(validateHotelData).map(hotel => ({
        ...hotel,
        name: sanitizeInput(hotel.name),
        city: sanitizeInput(hotel.city || ''),
        code: hotel.code || '', // Preservar código do hotel
        logo: hotel.logo || null
      }));
      
      setHotels(validatedHotels);
      
      // Buscar hotel selecionado do localStorage usando authService
      const selectedHotelData = authService.getSelectedHotel();
      
      if (selectedHotelData && validateHotelData(selectedHotelData)) {
        const sanitizedName = sanitizeInput(selectedHotelData.name);
        setSelectedHotel(sanitizedName);
      } else if (validatedHotels.length > 0) {
        const firstHotelName = sanitizeInput(validatedHotels[0].name);
        setSelectedHotel(firstHotelName);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar hotéis:', error);
      showNotificationModal('error', 'Erro ao Carregar Hotéis', 'Não foi possível carregar a lista de hotéis. Tente novamente.');
      setHotels([]);
      setSelectedHotel('');
    } finally {
      setLoadingHotels(false);
    }
  }, [isAuthenticated, validateHotelData, sanitizeInput, showNotificationModal]);

  useEffect(() => {
    loadHotels();
  }, [loadHotels]);

  // Escutar mudanças no hotel selecionado com validação
  useEffect(() => {
    const handleHotelChange = (event) => {
      const { hotel } = event.detail;
      try {
        const sanitizedName = sanitizeInput(hotel.name);
        setSelectedHotel(sanitizedName);
      } catch (e) {
        console.error('Erro ao processar mudança de hotel:', e);
      }
    };
    
    window.addEventListener('hotelChanged', handleHotelChange);
    
    return () => {
      window.removeEventListener('hotelChanged', handleHotelChange);
    };
  }, [sanitizeInput]);

  // Carregar notificações da API com lazy loading
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setLoadingNotifications(false);
      return;
    }

    try {
      setLoadingNotifications(true);
      const notificationsData = await notificationService.getAll();
      
      // Sanitizar dados das notificações
      const sanitizedNotifications = notificationsData.map(notification => ({
        ...notification,
        message: sanitizeInput(notification.message || ''),
        time: sanitizeInput(notification.time || '')
      }));
      
      setNotifications(sanitizedNotifications);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      showNotificationModal('error', 'Erro ao Carregar Notificações', 'Não foi possível carregar as notificações. Tente novamente.');
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, [isAuthenticated, sanitizeInput, showNotificationModal]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Funções de ação otimizadas
  const handleCreateReservation = useCallback(() => {
    if (onCreateReservation) {
      onCreateReservation();
    } else {
      setShowReservationModal(true);
    }
  }, [onCreateReservation]);

  const handleSaveReservation = useCallback(async (reservationData) => {
    try {
      setShowReservationModal(false);
      showNotificationModal('success', 'Reserva Criada', 'Reserva criada com sucesso!');
      // Recarregar a página para atualizar os dados
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Erro ao criar reserva:', error);
      showNotificationModal('error', 'Erro ao Criar Reserva', 'Erro ao criar reserva. Tente novamente.');
    }
  }, [showNotificationModal]);

  const handleSettings = useCallback(() => {
    setShowUserDropdown(false);
    navigate('/settings');
  }, [navigate]);

  const handleProfile = useCallback(() => {
    setShowUserDropdown(false);
    showNotificationModal('info', 'Funcionalidade Futura', 'Página de perfil será implementada em breve!');
  }, [showNotificationModal]);

  const handleHelp = useCallback(() => {
    setShowUserDropdown(false);
    showNotificationModal('info', 'Funcionalidade Futura', 'Página de ajuda será implementada em breve!');
  }, [showNotificationModal]);

  const handleLogout = useCallback(() => {
    setShowUserDropdown(false);
    authService.logout();
    navigate('/login');
  }, [navigate]);

  const handleMarkNotificationAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, unread: false }
            : notification
        )
      );
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      showNotificationModal('error', 'Erro ao Marcar Notificação', 'Não foi possível marcar a notificação como lida.');
    }
  }, [showNotificationModal]);

  const handleChangeHotel = useCallback(() => {
    navigate('/select-hotel');
  }, [navigate]);

  // Função para trocar de hotel com validação e tratamento de erro robusto
  const handleHotelSwitch = useCallback(async (hotel) => {
    if (!validateHotelData(hotel)) {
      showNotificationModal('error', 'Hotel Inválido', 'Dados do hotel são inválidos.');
      return;
    }

    try {
      
      setIsChangingHotel(true);
      setChangingHotelName(sanitizeInput(hotel.name));
      setShowHotelDropdown(false);
      
      // Salvar no localStorage
      authService.setSelectedHotel(hotel);
      setSelectedHotel(sanitizeInput(hotel.name));
      
      // Atualizar no backend
      const response = await api.post('/hotels/primary', { hotelId: hotel.id });
      
      // Atualizar o token se um novo foi fornecido
      if (response.data?.newToken) {
        localStorage.setItem('token', response.data.newToken);
      }
      
      // Emitir evento customizado para notificar outros componentes
      window.dispatchEvent(new CustomEvent('hotelChanged', { 
        detail: { hotel } 
      }));
      
      // Mostrar feedback visual e resetar loading após 1.5 segundos
      setTimeout(() => {
        setIsChangingHotel(false);
        setChangingHotelName('');
      }, 1500);
      
    } catch (error) {
      console.error('❌ Erro ao trocar hotel:', error);
      showNotificationModal('error', 'Erro ao Trocar Hotel', 'Erro ao trocar de hotel. Tente novamente.');
      setIsChangingHotel(false);
      setChangingHotelName('');
    }
  }, [validateHotelData, sanitizeInput, showNotificationModal]);

  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (showHotelDropdown && hotelDropdownRef.current && !hotelDropdownRef.current.contains(event.target)) {
        // Verificar se não é um click em um item do dropdown
        if (!event.target.closest('button[role="option"]')) {
          setShowHotelDropdown(false);
        }
      }
      if (showNotifications && notificationsDropdownRef.current && !notificationsDropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showHotelDropdown || showUserDropdown || showNotifications) {
      // Usar setTimeout para garantir que o click do dropdown seja processado primeiro
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showHotelDropdown, showUserDropdown, showNotifications]);

  return (
    <header 
      className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 backdrop-blur-sm bg-card/95"
      role="banner"
      aria-label="Cabeçalho principal"
    >
      {/* Overlay de alteração de hotel com melhor UX */}
      {isChangingHotel && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hotel-change-title"
          aria-describedby="hotel-change-description"
        >
          <div className="bg-card border border-border rounded-xl p-8 shadow-elevation-3 text-center max-w-md mx-4 animate-in fade-in-0 zoom-in-95 duration-300">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <h3 id="hotel-change-title" className="text-xl font-semibold text-foreground mb-2">
              Alterando Hotel
            </h3>
            <p id="hotel-change-description" className="text-muted-foreground mb-4">
              Alterando Hotel para <span className="font-semibold text-primary">"{changingHotelName}"</span>
            </p>
            <p className="text-sm text-muted-foreground">
              A página será recarregada automaticamente...
            </p>
            <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between h-full px-2 sm:px-4 md:px-6">
        {/* Menu Button - Mobile/Tablet com melhor acessibilidade */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-muted transition-all duration-200 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Abrir menu de navegação"
          aria-expanded="false"
          aria-controls="main-navigation"
        >
          <Icon name="Menu" size={20} color="var(--color-foreground)" />
        </button>

        {/* Logo responsiva com melhor performance e acessibilidade */}
        <div className="flex items-center flex-shrink-0">
          {(currentHotel?.logo || initialHotel?.logo) ? (
            <div 
              className="rounded-lg flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 w-10 h-10 sm:w-14 sm:h-14 md:w-20 md:h-20 lg:w-32 lg:h-32 xl:w-40 xl:h-40"
              role="img"
              aria-label={`Logo do hotel ${currentHotel?.name || initialHotel?.name || 'Hotel'}`}
            >
              <img 
                src={currentHotel?.logo || initialHotel?.logo} 
                alt={`Logo ${currentHotel?.name || initialHotel?.name}`}
                className="w-full h-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="bg-primary rounded-lg flex items-center justify-center hidden w-full h-full"
              >
                <Icon 
                  name="Hotel" 
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-12 md:h-12 lg:w-16 lg:h-16 xl:w-20 xl:h-20"
                  color="white" 
                />
              </div>
            </div>
          ) : (
            <div 
              className="w-10 h-10 sm:w-14 sm:h-14 md:w-20 md:h-20 lg:w-32 lg:h-32 xl:w-40 xl:h-40 bg-primary rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-shadow duration-200"
              role="img"
              aria-label="Logo padrão do sistema"
            >
              <Icon name="Hotel" size={16} className="sm:w-6 sm:h-6 md:w-12 md:h-12 lg:w-16 lg:h-16 xl:w-20 xl:h-20" color="white" />
            </div>
          )}
        </div>

        {/* Hotel Selector responsivo com melhor UX e acessibilidade */}
        <div className="flex justify-center min-w-0">
          {/* Desktop/Tablet - Seletor completo */}
          <div className="hidden sm:block">
          <div className="relative hotel-dropdown" ref={hotelDropdownRef}>
            <button
              onClick={() => setShowHotelDropdown(!showHotelDropdown)}
              className="flex items-center justify-between gap-2 px-2 md:px-3 lg:px-4 py-2 bg-muted rounded-lg border border-border hover:bg-muted/80 hover:border-primary/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 min-w-0 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl"
              disabled={loadingHotels || hotels.length === 0 || isChangingHotel}
              aria-label="Selecionar hotel"
              aria-expanded={showHotelDropdown}
              aria-haspopup="listbox"
              aria-describedby="hotel-selector-description"
            >
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <Icon name="Building2" size={16} color="var(--color-primary)" className="flex-shrink-0" />
                <div className="text-left min-w-0">
                  <div className="text-xs md:text-sm font-medium text-foreground whitespace-nowrap" title={selectedHotel}>
                    {loadingHotels ? 'Carregando...' : 
                     hotels.length === 0 ? 'Nenhum hotel disponível' : 
                     selectedHotel || 'Selecionar hotel'}
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap" title={currentHotel?.city}>
                    {loadingHotels ? 'Aguarde...' : 
                     hotels.length === 0 ? 'Entre em contato com o administrador' : 
                     currentHotel?.city || 'Escolha um hotel'}
                  </div>
                </div>
              </div>
              <Icon 
                name="ChevronDown" 
                size={16} 
                color="var(--color-muted-foreground)" 
                className={`flex-shrink-0 transition-transform duration-200 ${showHotelDropdown ? 'rotate-180' : ''}`} 
              />
            </button>
            <div id="hotel-selector-description" className="sr-only">
              Seletor de hotel atual: {selectedHotel || 'Nenhum hotel selecionado'}
            </div>

            {showHotelDropdown && (
              <div 
                className="absolute top-full left-0 mt-1 bg-popover border border-border rounded-lg shadow-elevation-3 z-60 max-h-96 overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-200 min-w-full"
                role="listbox"
                aria-label="Lista de hotéis disponíveis"
                style={{ width: 'max-content', minWidth: '100%' }}
              >
                <div className="p-2">
                  {hotels.length === 0 ? (
                    <div className="p-3 text-center text-muted-foreground">
                      <Icon name="Building2" size={24} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhum hotel disponível</p>
                      <p className="text-xs mt-1">Entre em contato com o administrador</p>
                    </div>
                  ) : (
                    hotels.map((hotel, index) => (
                      <button
                        key={hotel.id}
                        onClick={() => handleHotelSwitch(hotel)}
                        disabled={isChangingHotel}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-all duration-200 text-left min-w-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                          isChangingHotel ? 'opacity-50 cursor-not-allowed' : ''
                        } ${selectedHotel === hotel.name ? 'bg-primary/10 border border-primary/20' : ''}`}
                        role="option"
                        aria-selected={selectedHotel === hotel.name}
                        aria-label={`Hotel ${hotel.name} em ${hotel.city}`}
                      >
                        {hotel.logo ? (
                          <div className="w-10 h-10 rounded flex items-center justify-center overflow-hidden bg-white border border-border flex-shrink-0">
                            <img 
                              src={hotel.logo} 
                              alt={`Logo ${hotel.name}`}
                              className="w-full h-full object-contain"
                              loading="lazy"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="w-10 h-10 flex items-center justify-center hidden">
                              <Icon name="Building2" size={24} color="var(--color-primary)" />
                            </div>
                          </div>
                        ) : (
                          <Icon name="Building2" size={24} color="var(--color-primary)" className="flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground whitespace-nowrap" title={hotel.name}>{hotel.name}</div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap" title={hotel.city}>{hotel.city}</div>
                        </div>
                        {selectedHotel === hotel.name && (
                          <Icon name="Check" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

          {/* Mobile - Botão compacto com nome do hotel */}
          <div className="sm:hidden">
            <div className="relative hotel-dropdown" ref={hotelDropdownRef}>
              <button
                onClick={() => setShowHotelDropdown(!showHotelDropdown)}
                className="flex items-center gap-2 px-2 py-2 bg-muted rounded-lg border border-border hover:bg-muted/80 hover:border-primary/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 min-w-0 max-w-xs"
                disabled={loadingHotels || hotels.length === 0 || isChangingHotel}
                aria-label="Trocar hotel"
                aria-expanded={showHotelDropdown}
                aria-haspopup="listbox"
                aria-describedby="hotel-selector-mobile-description"
                title={selectedHotel || 'Trocar hotel'}
              >
                <Icon name="Building2" size={14} color="var(--color-primary)" className="flex-shrink-0" />
      <span className="text-xs font-medium text-foreground whitespace-nowrap min-w-0" title={selectedHotel}>
        {loadingHotels ? 'Carregando...' : 
         hotels.length === 0 ? 'N/A' : 
         selectedHotel || 'Hotel'}
      </span>
              </button>
              <div id="hotel-selector-mobile-description" className="sr-only">
                Botão para trocar hotel atual: {selectedHotel || 'Nenhum hotel selecionado'}
              </div>

              {showHotelDropdown && (
                <div 
                  className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-elevation-3 z-60 max-h-96 overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-200"
                  role="listbox"
                  aria-label="Lista de hotéis disponíveis"
                >
                  <div className="p-2">
                    {hotels.length === 0 ? (
                      <div className="p-3 text-center text-muted-foreground">
                        <Icon name="Building2" size={24} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhum hotel disponível</p>
                        <p className="text-xs mt-1">Entre em contato com o administrador</p>
                      </div>
                    ) : (
                      hotels.map((hotel, index) => (
                        <button
                          key={hotel.id}
                          onClick={() => handleHotelSwitch(hotel)}
                          disabled={isChangingHotel}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-all duration-200 text-left min-w-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                            isChangingHotel ? 'opacity-50 cursor-not-allowed' : ''
                          } ${selectedHotel === hotel.name ? 'bg-primary/10 border border-primary/20' : ''}`}
                          role="option"
                          aria-selected={selectedHotel === hotel.name}
                          aria-label={`Hotel ${hotel.name} em ${hotel.city}`}
                        >
                          {hotel.logo ? (
                            <div className="w-8 h-8 rounded flex items-center justify-center overflow-hidden bg-white border border-border flex-shrink-0">
                              <img 
                                src={hotel.logo} 
                                alt={`Logo ${hotel.name}`}
                                className="w-full h-full object-contain"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="w-8 h-8 flex items-center justify-center hidden">
                                <Icon name="Building2" size={20} color="var(--color-primary)" />
                              </div>
                            </div>
                          ) : (
                            <Icon name="Building2" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-foreground truncate">{hotel.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{hotel.city}</div>
                          </div>
                          {selectedHotel === hotel.name && (
                            <Icon name="Check" size={16} color="var(--color-primary)" className="flex-shrink-0" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - Ações responsivas */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
          {/* Landing Page Link */}
          {(() => {
            const hotelCode = currentHotel?.code || initialHotel?.code;
            if (!hotelCode) return null;
            
            return (
              <button
                onClick={() => {
                  const normalizedCode = hotelCode.toLowerCase().trim();
                  const landingUrl = `/hotel/${normalizedCode}`;
                  window.open(landingUrl, '_blank', 'noopener,noreferrer');
                }}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                title={`Abrir landing page do hotel (${hotelCode}) em nova aba`}
                aria-label="Abrir landing page do hotel"
              >
                <Icon name="ExternalLink" size={16} className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" color="var(--color-muted-foreground)" />
              </button>
            );
          })()}

          {/* Notifications com melhor UX */}
          <div className="relative notifications-dropdown" ref={notificationsDropdownRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
              aria-expanded={showNotifications}
              aria-haspopup="menu"
            >
              <Icon name="Bell" size={16} className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" color="var(--color-muted-foreground)" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center animate-pulse">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div 
                className="absolute top-full right-0 mt-1 w-80 bg-popover border border-border rounded-lg shadow-elevation-3 z-60 animate-in fade-in-0 zoom-in-95 duration-200"
                role="menu"
                aria-label="Menu de notificações"
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">Notificações</h3>
                    {unreadCount > 0 && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full">
                        {unreadCount} não lidas
                      </span>
                    )}
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {loadingNotifications ? (
                    <div className="p-4 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Carregando notificações...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-4 text-center">
                      <Icon name="Bell" size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm text-muted-foreground">Nenhuma notificação</p>
                      <p className="text-xs text-muted-foreground mt-1">Você está em dia!</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-border last:border-b-0 hover:bg-muted/50 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                          notification.unread ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                        }`}
                        onClick={() => handleMarkNotificationAsRead(notification.id)}
                        role="menuitem"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMarkNotificationAsRead(notification.id);
                          }
                        }}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            notification.unread ? 'bg-primary' : 'bg-muted'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground break-words">{notification.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notification.time}</p>
                          </div>
                          {notification.unread && (
                            <Icon name="Circle" size={8} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 border-t border-border">
                  <Button variant="ghost" size="sm" className="w-full">
                    Ver todas as notificações
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions com melhor UX */}
          <Button 
            variant="default" 
            size="sm" 
            iconName="Plus" 
            iconPosition="left"
            onClick={handleCreateReservation}
            className="hidden md:flex hover:scale-105 transition-transform duration-200"
            aria-label="Criar nova reserva"
          >
            Reserva
          </Button>
          
          {/* Mobile Quick Action - Icon Only */}
          <Button 
            variant="default" 
            size="sm" 
            iconName="Plus" 
            onClick={handleCreateReservation}
            className="md:hidden hover:scale-105 transition-transform duration-200 p-1.5"
            title="Criar nova reserva"
            aria-label="Criar nova reserva"
          />
          
          {/* Change Hotel Button - Hidden on small tablets, icon only on medium tablets */}
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Building2" 
            iconPosition="left"
            onClick={handleChangeHotel}
            title="Trocar de hotel"
            className="hidden lg:flex hover:scale-105 transition-transform duration-200"
            aria-label="Trocar de hotel"
          >
            Trocar Hotel
          </Button>
          
          {/* Tablet - Icon Only */}
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Building2" 
            onClick={handleChangeHotel}
            title="Trocar de hotel"
            className="hidden md:flex lg:hidden hover:scale-105 transition-transform duration-200 p-1.5"
            aria-label="Trocar de hotel"
          />

          {/* User Menu com melhor UX e acessibilidade */}
          <div className="relative user-dropdown" ref={userDropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center justify-center p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              title={user?.name || 'Menu do usuário'}
              aria-label={`Menu do usuário${user?.name ? ` - ${user.name}` : ''}`}
              aria-expanded={showUserDropdown}
              aria-haspopup="menu"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-primary rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <Icon name="User" size={14} className="sm:w-4 sm:h-4 lg:w-5 lg:h-5" color="white" />
              </div>
            </button>

            {showUserDropdown && (
              <div 
                className="absolute top-full right-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-elevation-3 z-60 animate-in fade-in-0 zoom-in-95 duration-200"
                role="menu"
                aria-label="Menu do usuário"
              >
                <div className="p-2">
                  <div className="px-3 py-2 border-b border-border mb-1">
                    <p className="text-sm font-medium text-foreground truncate">{user?.name || 'Usuário'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
                  </div>
                  
                  <button 
                    onClick={handleProfile}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-muted transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                    role="menuitem"
                  >
                    <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground">Meu Perfil</span>
                  </button>
                  
                  <button 
                    onClick={handleSettings}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-muted transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                    role="menuitem"
                  >
                    <Icon name="Settings" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground">Configurações</span>
                  </button>
                  
                  <button 
                    onClick={handleHelp}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-muted transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                    role="menuitem"
                  >
                    <Icon name="HelpCircle" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground">Ajuda</span>
                  </button>

                  <div className="border-t border-border my-1" />
                  
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-error/10 transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-1"
                    role="menuitem"
                  >
                    <Icon name="LogOut" size={16} color="var(--color-error)" />
                    <span className="text-sm text-error">Sair</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      <ReservationModal
        isOpen={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        reservation={null}
        onSave={handleSaveReservation}
        mode="create"
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={handleCloseNotification}
        type={notificationData.type}
        title={notificationData.title}
        message={notificationData.message}
        details={notificationData.details}
      />
    </header>
  );
};

export default Header;